import { COOKIE_KEY } from '@/lib/Constants';
import { getCookieForApp, getOsType, getTokenForApp, setCookieForApp } from '@/lib/CookieUtils';
import { Endpoints } from '@/lib/Endpoints';
import { sendCRODataLayerEvent } from '@/lib/gtm-wrapper';
import { getApiBaseUrl } from '@/lib/apiBaseUrl';
import { isAppClient } from '@/lib/LocalStorage';
import { safeSessionStorage } from '@/lib/safeStorage';

export const AB_KEYS = {
  checkout: 'checkout',
  checkoutOptional: 'checkout_optional',
  gridBanner: 'grid_banner',
  thyIntegration: 'thy_integration',
} as const;

export type AbExperimentKey = (typeof AB_KEYS)[keyof typeof AB_KEYS];

export type AbExperimentMap = Record<string, string>;

const SEEN_PREFIX = 'hzc_ab_seen:';

let memoryMap: AbExperimentMap = {};

export function parseExperiments(raw: unknown): AbExperimentMap {
  if (!raw) {
    return {};
  }
  let source: unknown = raw;
  if (typeof raw === 'string') {
    try {
      source = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (!source || typeof source !== 'object' || Array.isArray(source)) {
    return {};
  }
  const map: AbExperimentMap = {};
  Object.entries(source as Record<string, unknown>).forEach(([key, value]) => {
    const experimentKey = String(key || '').trim().toLowerCase();
    if (experimentKey.startsWith('__')) return;
    const variant = String(value || '').trim().toLowerCase();
    if (experimentKey && variant) {
      map[experimentKey] = variant;
    }
  });
  return map;
}

export function serializeExperiments(map: AbExperimentMap): string {
  return JSON.stringify(map || {});
}

const AB_USER_KEY = '__uid';

export function readAbAssignment(raw: unknown): { experiments: AbExperimentMap; userId: string } {
  let source: unknown = raw;
  if (typeof raw === 'string') {
    try {
      source = JSON.parse(raw);
    }
    catch {
      source = null;
    }
  }
  const record = source && typeof source === 'object' && !Array.isArray(source)
    ? source as Record<string, unknown>
    : {};
  return {
    experiments: parseExperiments(record),
    userId: record[AB_USER_KEY] == null ? '' : String(record[AB_USER_KEY]),
  };
}

export function serializeAbAssignment(experiments: AbExperimentMap, userId?: string): string {
  const body: Record<string, string> = { ...(experiments || {}) };
  if (userId) body[AB_USER_KEY] = String(userId);
  return JSON.stringify(body);
}

export function userIdFromToken(token?: string): string {
  const part = String(token || '').split('.')[1];
  if (!part) return '';
  try {
    const padded = part.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(padded));
    return json && json.u != null ? String(json.u) : '';
  }
  catch {
    return '';
  }
}

export function saveExperiments(map: AbExperimentMap, opts?: { identityChanged?: boolean }) {
  const next = parseExperiments(map);
  memoryMap = next;
  if (typeof window !== 'undefined') {
    setCookieForApp(COOKIE_KEY.AB, serializeExperiments(next));
    if (opts?.identityChanged) {
      clearSeenKeys();
    }
    window.dispatchEvent(new CustomEvent('hzc:ab-experiments-updated'));
  }
  return next;
}

export function applyExperimentsFromPayload(data: { experiments?: unknown } | null | undefined) {
  if (!data) {
    return {};
  }
  return saveExperiments(parseExperiments(data.experiments), { identityChanged: true });
}

export function sameExperiments(left: AbExperimentMap, right: AbExperimentMap): boolean {
  const keys = new Set([...Object.keys(left || {}), ...Object.keys(right || {})]);
  for (const key of keys) {
    if ((left?.[key] || '') !== (right?.[key] || '')) return false;
  }
  return true;
}

export function getExperiments(): AbExperimentMap {
  if (typeof window === 'undefined') {
    return { ...memoryMap };
  }
  const fromCookie = parseExperiments(getCookieForApp(COOKIE_KEY.AB));
  if (Object.keys(fromCookie).length > 0) {
    memoryMap = fromCookie;
    return { ...fromCookie };
  }
  return { ...memoryMap };
}

let refreshInflight: Promise<AbExperimentMap | null> | null = null;

async function refreshExperimentsNow(): Promise<AbExperimentMap | null> {
  const token = getTokenForApp();
  if (!token) return null;
  const osType = (getOsType() || 'web').toLowerCase();
  try {
    const response = await fetch(`${getApiBaseUrl()}${Endpoints.AB_EXPERIMENTS}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        token,
      },
      body: JSON.stringify({ osType }),
    });
    if (!response.ok) return null;
    const body = await response.json();
    if (body?.data?.experiments == null) return null;
    const experiments = parseExperiments(body.data.experiments);
    const stored = readAbAssignment(getCookieForApp(COOKIE_KEY.AB));
    const userId = stored.userId || userIdFromToken(token);
    memoryMap = experiments;
    setCookieForApp(COOKIE_KEY.AB, serializeAbAssignment(experiments, userId));
    window.dispatchEvent(new CustomEvent('hzc:ab-experiments-updated'));
    return experiments;
  } catch {
    return null;
  }
}

/** Panel yüzdeleri değişince çerezdeki eski varyant kalmasın. */
export function refreshExperiments(): Promise<AbExperimentMap | null> {
  if (typeof window === 'undefined' || isAppClient()) return Promise.resolve(null);
  if (!refreshInflight) {
    refreshInflight = refreshExperimentsNow().finally(() => {
      refreshInflight = null;
    });
  }
  return refreshInflight;
}

export function getVariant(experimentKey: string): string | undefined {
  const key = String(experimentKey || '').trim().toLowerCase();
  if (!key) {
    return undefined;
  }
  return getExperiments()[key];
}

export function isVariant(experimentKey: string, variant: string): boolean {
  const expected = String(variant || '').trim().toLowerCase();
  if (!expected) {
    return false;
  }
  return getVariant(experimentKey) === expected;
}

const BANNER_AB_TEST_NAME = 'Test No 2 - Banner';

export function pushBannerAbTest(variant: string) {
  if (typeof window === 'undefined' || isAppClient()) return;
  const isV1 = String(variant || '').trim().toLowerCase() === 'b';
  sendCRODataLayerEvent({ ecommerce: null });
  sendCRODataLayerEvent({
    event: 'gtm_ab_test',
    gtm_ab_test_name: BANNER_AB_TEST_NAME,
    gtm_ab_test_variation: isV1
      ? `${BANNER_AB_TEST_NAME} - V1`
      : `${BANNER_AB_TEST_NAME} - Control`,
  });
}

export function markExperimentSeen(experimentKey: string) {
  if (typeof window === 'undefined' || isAppClient()) {
    return;
  }
  const key = String(experimentKey || '').trim().toLowerCase();
  const variant = getVariant(key);
  if (!key || !variant) {
    return;
  }
  const seenKey = `${SEEN_PREFIX}${key}:${variant}`;
  if (safeSessionStorage.getItem(seenKey)) {
    return;
  }
  safeSessionStorage.setItem(seenKey, '1');

  sendCRODataLayerEvent({
    event: 'experiment_exposure',
    experiment_key: key,
    variant_key: variant,
  });

  const token = getTokenForApp();
  if (!token) {
    return;
  }

  const osType = (getOsType() || 'web').toLowerCase();
  void fetch(`${getApiBaseUrl()}${Endpoints.AB_EXPERIMENT_MARK_SEEN}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      token,
    },
    body: JSON.stringify({
      experimentKey: key,
      variantKey: variant,
      osType,
    }),
    keepalive: true,
  }).catch(() => undefined);
}

function clearSeenKeys() {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const storage = window.sessionStorage;
    const toRemove: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && key.startsWith(SEEN_PREFIX)) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((key) => storage.removeItem(key));
  } catch {
    // ignore
  }
}
